import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraHeading, SpecraPanelHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageBody, PageHero } from '../../components/layout';
import { useRecordShellEnabled } from '../../components/RecordShell/recordShellFlag';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { EconomicsFigures } from '../Landscapes/components/EconomicsFigures';
import { usePortfolioEconomics } from '../Landscapes/hooks/usePortfolioEconomics';
import { GrowthHomeV2 } from './GrowthHomeV2';
import { useGrowthHomeLens } from './growthHomeLens';
import { useGrowthHomeWork } from './useGrowthHomeWork';
/**
 * `/growth/home`. GRW-22: with growth_record_shell_v1 on it is Growth Home v2
 * (mockup GrowthHome.png); off, today's page, untouched.
 */ export default function GrowthHomePage() {
    return useRecordShellEnabled() ? /*#__PURE__*/ _jsx(GrowthHomeV2, {}) : /*#__PURE__*/ _jsx(LegacyGrowthHomePage, {});
}
function LegacyGrowthHomePage() {
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    const [lens, select] = useGrowthHomeLens();
    const economics = usePortfolioEconomics();
    const work = useGrowthHomeWork(lens);
    const loading = economics.isLoading || work.isLoading;
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.growthHome.page,
        children: /*#__PURE__*/ _jsxs(PageBody, {
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Growth Home",
                    subtitle: "Your authorized company portfolio and active work",
                    actions: /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.growthHome.my,
                                "aria-pressed": lens === 'my',
                                variant: lens === 'my' ? 'default' : 'outline',
                                onClick: ()=>select('my'),
                                children: "My work"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.growthHome.team,
                                "aria-pressed": lens === 'team',
                                variant: lens === 'team' ? 'default' : 'outline',
                                onClick: ()=>select('team'),
                                children: "Team work"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        loading && /*#__PURE__*/ _jsx("output", {
                            "data-testid": Testids.growthHome.loading,
                            children: "Loading Growth Home…"
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCard, {
                            "data-testid": Testids.growthHome.portfolio,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                                    title: "Company portfolio",
                                    subtitle: "Opportunity economics · authorized company context, unchanged by your work lens"
                                }),
                                /*#__PURE__*/ _jsx(SpecraCardContent, {
                                    children: economics.isError ? /*#__PURE__*/ _jsxs("div", {
                                        role: "alert",
                                        children: [
                                            "Company economics are unavailable or access is denied.",
                                            ' ',
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "outline",
                                                onClick: ()=>economics.refetch(),
                                                children: "Retry company portfolio"
                                            })
                                        ]
                                    }) : economics.data?.portfolio && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(EconomicsFigures, {
                                                figures: economics.data.portfolio,
                                                showWorkload: false
                                            }),
                                            economics.data.portfolio.opportunityCount === BigInt(0) && /*#__PURE__*/ _jsx("p", {
                                                children: "No Opportunities in your authorized company portfolio."
                                            })
                                        ]
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCard, {
                            "data-testid": Testids.growthHome.landscapes,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                                    title: "Landscapes"
                                }),
                                /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: "/landscapes",
                                            children: "Explore Landscapes"
                                        }),
                                        economics.isError ? /*#__PURE__*/ _jsxs("div", {
                                            role: "alert",
                                            children: [
                                                "Landscapes are unavailable or access is denied.",
                                                ' ',
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    variant: "outline",
                                                    onClick: ()=>economics.refetch(),
                                                    children: "Retry Landscapes"
                                                })
                                            ]
                                        }) : economics.isLoading ? /*#__PURE__*/ _jsx("output", {
                                            children: "Loading Landscapes…"
                                        }) : economics.data?.landscapes.map((landscape)=>/*#__PURE__*/ _jsx("p", {
                                                children: /*#__PURE__*/ _jsx(Link, {
                                                    to: `/landscapes/${landscape.landscapeId}`,
                                                    children: landscape.name
                                                })
                                            }, landscape.landscapeId)),
                                        !economics.isError && !economics.isLoading && economics.data && economics.data.landscapes.length === 0 && /*#__PURE__*/ _jsx("p", {
                                            children: "No authorized Landscapes yet."
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCard, {
                            "data-testid": Testids.growthHome.work,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                                    title: lens === 'my' ? 'My active work' : 'Team active work',
                                    subtitle: "Separate workspace counts within your permitted scope"
                                }),
                                /*#__PURE__*/ _jsx(SpecraCardContent, {
                                    children: work.isError ? /*#__PURE__*/ _jsxs("div", {
                                        role: "alert",
                                        children: [
                                            "Work is unavailable or access is denied. Try again.",
                                            ' ',
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "outline",
                                                onClick: ()=>work.refetch(),
                                                children: "Retry work"
                                            })
                                        ]
                                    }) : !work.isLoading && work.data && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs("dl", {
                                                className: "flex gap-8",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("dt", {
                                                                children: "Pursuits"
                                                            }),
                                                            /*#__PURE__*/ _jsx("dd", {
                                                                "data-testid": Testids.growthHome.pursuits,
                                                                children: work.data.pursuits
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("dt", {
                                                                children: "Proposals"
                                                            }),
                                                            /*#__PURE__*/ _jsx("dd", {
                                                                "data-testid": Testids.growthHome.proposals,
                                                                children: work.data.proposals
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("dt", {
                                                                children: "RFIs"
                                                            }),
                                                            /*#__PURE__*/ _jsx("dd", {
                                                                "data-testid": Testids.growthHome.rfis,
                                                                children: work.data.rfis
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            work.data.items.length === 0 && /*#__PURE__*/ _jsx("p", {
                                                "data-testid": Testids.growthHome.empty,
                                                children: lens === 'my' ? 'No personal work yet. Choose Team work to explore permitted work; your company portfolio remains above.' : 'No active work in your permitted team scope.'
                                            }),
                                            /*#__PURE__*/ _jsx("ul", {
                                                children: work.data.items.map((item)=>/*#__PURE__*/ _jsxs("li", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Link, {
                                                                "data-testid": Testids.growthHome.workItem,
                                                                "data-work-kind": item.kind,
                                                                "data-work-id": item.id,
                                                                to: item.href,
                                                                children: item.title
                                                            }),
                                                            ' ',
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "text-muted-foreground",
                                                                children: item.kind
                                                            })
                                                        ]
                                                    }, `${item.kind}:${item.id}`))
                                            })
                                        ]
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCard, {
                            "data-testid": Testids.growthHome.attention,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                                    title: "Needs attention",
                                    subtitle: "Material market and Pursuit changes, assigned tasks and review/deadline pressure · visible context in this lens"
                                }),
                                /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                    children: [
                                        work.isLoading && /*#__PURE__*/ _jsx("output", {
                                            children: /*#__PURE__*/ _jsx(SpecraText, {
                                                children: "Loading changes…"
                                            })
                                        }),
                                        work.data && !work.isError && !work.isLoading && /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraHeading, {
                                                    as: "h3",
                                                    size: "sm",
                                                    children: "Market changes"
                                                }),
                                                work.data.changesUnavailable ? /*#__PURE__*/ _jsxs(SpecraText, {
                                                    role: "alert",
                                                    children: [
                                                        "Source changes are unavailable or access is denied.",
                                                        ' ',
                                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                                            variant: "outline",
                                                            onClick: ()=>work.refetch(),
                                                            children: "Retry source changes"
                                                        })
                                                    ]
                                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        (work.data.changes ?? []).length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                                                            children: "No recorded source changes for visible work in this lens."
                                                        }),
                                                        /*#__PURE__*/ _jsx("ul", {
                                                            className: "space-y-4",
                                                            children: (work.data.changes ?? []).map((change)=>/*#__PURE__*/ _jsxs("li", {
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(Link, {
                                                                            to: change.href,
                                                                            children: change.title
                                                                        }),
                                                                        /*#__PURE__*/ _jsx(SpecraText, {
                                                                            as: "p",
                                                                            children: change.explanation
                                                                        }),
                                                                        /*#__PURE__*/ _jsx(SpecraText, {
                                                                            as: "p",
                                                                            size: "meta",
                                                                            children: change.source
                                                                        }),
                                                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                                                            as: "p",
                                                                            size: "meta",
                                                                            children: [
                                                                                "Recorded: ",
                                                                                change.recordedAt || 'Unknown',
                                                                                " · Published:",
                                                                                ' ',
                                                                                change.publishedAt || 'Unknown',
                                                                                " · Retrieved:",
                                                                                ' ',
                                                                                change.retrievedAt || 'Unknown'
                                                                            ]
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("ul", {
                                                                            children: change.contexts.map((context)=>/*#__PURE__*/ _jsxs("li", {
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsxs(Link, {
                                                                                            to: context.href,
                                                                                            children: [
                                                                                                context.title,
                                                                                                change.contexts.some((other)=>other.id !== context.id && other.kind === context.kind && other.title === context.title) && ` · ${context.id}`
                                                                                            ]
                                                                                        }),
                                                                                        ' ',
                                                                                        /*#__PURE__*/ _jsx(SpecraText, {
                                                                                            as: "span",
                                                                                            size: "meta",
                                                                                            children: context.kind
                                                                                        })
                                                                                    ]
                                                                                }, `${context.kind}:${context.id}`))
                                                                        })
                                                                    ]
                                                                }, change.id))
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraHeading, {
                                                    as: "h3",
                                                    size: "sm",
                                                    children: "Work needing attention"
                                                }),
                                                work.data.attention.length === 0 && /*#__PURE__*/ _jsx("p", {
                                                    children: "No assigned tasks or dated responses/reviews need attention in this lens."
                                                }),
                                                /*#__PURE__*/ _jsx("ul", {
                                                    children: work.data.attention.map((item)=>/*#__PURE__*/ _jsxs("li", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Link, {
                                                                    to: item.href,
                                                                    children: item.title
                                                                }),
                                                                /*#__PURE__*/ _jsx("p", {
                                                                    children: item.explanation
                                                                }),
                                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                                    as: "p",
                                                                    size: "meta",
                                                                    children: [
                                                                        item.source,
                                                                        " · ",
                                                                        item.dateLabel,
                                                                        " ",
                                                                        item.date
                                                                    ]
                                                                })
                                                            ]
                                                        }, item.id))
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("details", {
                            "data-testid": Testids.growthHome.definitions,
                            children: [
                                /*#__PURE__*/ _jsx("summary", {
                                    children: "Definitions, date and coverage"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "As of ",
                                        work.data?.asOf ?? 'loading',
                                        ". Coverage: currently authorized records only; missing values are unknown, not zero. Company economics are read independently of your working lens."
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: "Deal: estimated Opportunity value, counted once across overlapping Landscapes. Share: expected company portion. Bid: submitted price. Award: awarded value. Revenue: authorized actual revenue. Categories and currencies stay separate."
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: "Work counts distinct active Pursuit, Proposal and RFI workspaces. My work includes Pursuit ownership or open task assignment and direct bid/response team assignments; view-only bid/response observers appear in Team work. Team work includes all work you may read through these owning lists. Attention shows open assigned Pursuit tasks and saved deadlines for draft or in-progress RFI responses and open Proposal reviews. Explicit Pursuit lifecycle and outcome decisions retain their recorded dates, including parked or closed work; they do not add to active-work counts. Checked dates are read times when task source dates are missing. Market changes group each recorded amendment, confirmed evidence, relationship correction or award signal once per canonical Opportunity, with only visible contexts. Your active Watches appear in both lenses without assigning work or granting workspace access. My Landscape context includes Landscapes you created. Recording and retrieval dates are not publication dates; an unknown source time stays unknown."
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
